import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { organizationsAPI } from '../services/api';
import { useAuth } from './AuthContext';

const OrgContext = createContext(null);

export const OrgProvider = ({ children }) => {
  const { user } = useAuth();
  const [userOrgs, setUserOrgs] = useState([]);
  const [activeOrg, setActiveOrgState] = useState(() => {
    // Initialize from localStorage synchronously to avoid flash
    const savedOrgId = localStorage.getItem('deco_active_org');
    return savedOrgId ? { id: savedOrgId } : null;
  });
  const [loading, setLoading] = useState(false);
  const loadedForUser = useRef(null);

  // Load orgs once per user (not on every render)
  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      setUserOrgs([]);
      setActiveOrgState(null);
      loadedForUser.current = null;
      return;
    }

    // Don't re-fetch if we already loaded for this user
    if (loadedForUser.current === userId) return;
    loadedForUser.current = userId;

    setLoading(true);
    organizationsAPI.getAll()
      .then(response => {
        const orgs = response.data.organizations || [];
        setUserOrgs(orgs);

        // DEC-713: activeOrg is never null. Resolve in priority order:
        //   1. Saved org from localStorage (if user is still a member)
        //   2. User's personal org (every user has one post-DEC-712)
        //   3. First org in the list (defensive fallback if personal org is missing)
        const savedOrgId = localStorage.getItem('deco_active_org');
        const savedOrg = savedOrgId ? orgs.find(o => o.id === savedOrgId) : null;
        const personalOrg = orgs.find(o => o.personal_for_user_id === userId);
        const resolved = savedOrg || personalOrg || orgs[0] || null;

        if (resolved) {
          setActiveOrgState(resolved);
          localStorage.setItem('deco_active_org', resolved.id);
        } else {
          localStorage.removeItem('deco_active_org');
          setActiveOrgState(null);
        }
      })
      .catch(() => setUserOrgs([]))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const setActiveOrg = useCallback((org) => {
    if (org) {
      localStorage.setItem('deco_active_org', org.id);
      setActiveOrgState(org);
    } else {
      localStorage.removeItem('deco_active_org');
      setActiveOrgState(null);
    }
  }, []);

  const refreshOrgs = useCallback(() => {
    loadedForUser.current = null; // Force re-fetch
  }, []);

  // Stable context value — only changes when actual data changes
  const value = useMemo(() => ({
    userOrgs,
    activeOrg,
    setActiveOrg,
    loading,
    refreshOrgs,
  }), [userOrgs, activeOrg, setActiveOrg, loading, refreshOrgs]);

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};

export default OrgContext;
